import type { MetaPreviewFormat } from '@nono/contract';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, X } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/*
 * The ad itself, as people see it — Meta's own rendered preview for a chosen
 * placement, next to every text and headline the ad rotates through. The
 * preview URL is signed by Meta and short-lived, so it is fetched when opened.
 */

const FORMATS: { value: MetaPreviewFormat; label: string }[] = [
  { value: 'MOBILE_FEED_STANDARD', label: 'Facebook' },
  { value: 'INSTAGRAM_STANDARD', label: 'Instagram' },
  { value: 'INSTAGRAM_REELS', label: 'Reels' },
  { value: 'INSTAGRAM_STORY', label: 'Story' },
];
const TALL: MetaPreviewFormat[] = ['INSTAGRAM_REELS', 'INSTAGRAM_STORY', 'FACEBOOK_REELS_MOBILE'];

export function AdPreview({ adId, name, onClose }: { adId: string; name: string; onClose: () => void }) {
  const [format, setFormat] = useState<MetaPreviewFormat>('MOBILE_FEED_STANDARD');
  const q = useQuery({
    queryKey: ['meta-preview', adId, format],
    queryFn: () => api.metaAdPreview(adId, format),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
  const p = q.data;
  const tall = TALL.includes(format);

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-[var(--overlay)] p-2 backdrop-blur-sm sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <section
        role="dialog"
        aria-label={`Annonsen: ${name}`}
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
      >
        <header className="flex items-start gap-3 border-b border-border px-3 py-2.5 sm:px-4">
          <div className="min-w-0 flex-1">
            <div className="text-micro font-semibold uppercase tracking-wider text-fg-subtle">Annonsen</div>
            <div className="truncate text-section text-fg">{name}</div>
          </div>
          {p && (
            <a
              href={p.adsManagerUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden h-8 shrink-0 items-center gap-1.5 rounded-full border border-border px-3 text-label text-fg-muted hover:text-fg sm:flex"
            >
              Ads Manager <ExternalLink className="size-3.5" aria-hidden />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Lukk"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3 sm:flex-row sm:p-4">
          <div className="flex shrink-0 flex-col items-center gap-2">
            <div className="inline-flex rounded-full border border-border bg-surface p-0.5">
              {FORMATS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  aria-pressed={format === f.value}
                  onClick={() => setFormat(f.value)}
                  className={cn(
                    'h-8 whitespace-nowrap rounded-full px-3 text-label transition-fast',
                    format === f.value ? 'bg-surface-3 font-medium text-fg' : 'text-fg-muted hover:text-fg',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div
              className={cn(
                'relative flex w-[340px] max-w-full items-center justify-center overflow-hidden rounded-lg border border-border bg-white',
                tall ? 'h-[600px]' : 'h-[560px]',
              )}
            >
              {q.isLoading && <Spinner />}
              {p?.iframeSrc ? (
                <iframe
                  key={p.iframeSrc}
                  src={p.iframeSrc}
                  title={`Forhåndsvisning: ${name}`}
                  className="h-full w-full border-0"
                  scrolling="yes"
                />
              ) : p?.thumbnailUrl ? (
                <img src={p.thumbnailUrl} alt="" className="max-h-full max-w-full object-contain" />
              ) : q.isError || (p && !p.iframeSrc) ? (
                <p className="px-6 text-center text-label text-neutral-500">
                  Meta ga ingen forhåndsvisning for dette formatet. Prøv et annet, eller åpne i Ads Manager.
                </p>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 flex-1">
            {p && p.titles.length > 0 && (
              <TextList title={`Overskrifter (${p.titles.length})`} items={p.titles} />
            )}
            {p && p.bodies.length > 0 && <TextList title={`Tekster (${p.bodies.length})`} items={p.bodies} />}
            {p && !p.titles.length && !p.bodies.length && (
              <p className="text-label text-fg-muted">Ingen tekster funnet på annonsen.</p>
            )}
            {p && (
              <p className="mt-3 text-micro text-fg-subtle">
                Meta roterer mellom tekstene og overskriftene og viser hver person den kombinasjonen den tror virker best.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function TextList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 text-micro font-semibold uppercase tracking-wider text-fg-subtle">{title}</div>
      <ol className="flex flex-col gap-1.5">
        {items.map((t, i) => (
          <li key={t} className="flex gap-2 rounded-md bg-surface-2 p-2.5 text-label text-fg">
            <span className="tabular shrink-0 text-fg-subtle">{i + 1}</span>
            <span className="whitespace-pre-line">{t}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
